import { perceptibleReciprocal } from "./perceptibleReciprocal";

/**
 * Array of numbers.
 */
export type Vector = number[];

/**
 * Array of vectors.
 */
export type Matrix = Vector[];

/**
 * Solves system of equations using Gauss-Jordan elimination.
 *
 * Brings the matrix to reduced row echelon form, while simultaneously reducing and thus solving the augmented results
 * matrix.
 *
 * See also http://en.wikipedia.org/wiki/Gauss-Jordan_elimination
 *
 *
 *  Note that the 'matrix' is given as a 'array of arrays' of rank size. That is values can be assigned
 *  as   matrix[row][column]   where 'row' is typically the equation, and 'column' is the term of the equation.
 *  That is the matrix is in the form of a 'row first array'.
 *
 *  However, 'vectors' is a 'array of arrays' which can have any number of columns, with each column array the same
 *  'rank' size as 'matrix'.
 *
 *  This allows for simpler handling of the results, especially is only one column 'vector' is all that is required
 *  to produce the desired solution.
 *
 *  For example, the 'vectors' can consist of simple array of numbers.  when only one set of simultaneous equations is
 *  to be solved from the given set of coefficient weighted terms.
 *
 *  However, by specifying more 'columns' (as an 'array of vector columns', you can use this function to solve a set of
 *  'separable' equations.
 *
 *  For example a distortion function where
 *  ```
 *  u = U(x,y)  v = V(x,y)
 *  ```
 *  And the functions U() and V() have separate coefficients, but are being generated from a common x,y->u,v  data set.
 *
 *  You can also use the 'vectors' to generate an inverse of the given 'matrix' though as a 'column first array' rather
 *  than a 'row first array'. For details see http://en.wikipedia.org/wiki/Gauss-Jordan_elimination
 *
 *
 * @see https://imagemagick.org/api/MagickCore/matrix_8c_source.html#l00480 GaussJordanElimination() at
 * ImageMagick source.
 *
 * @param coefficients Linear equations system coefficients.
 * @param results Augment results matrix.
 */
export function gaussJordanElimination(
  coefficients: Matrix,
  results: Matrix
): Matrix {
  validate(coefficients, results);
  coefficients = cloneMatrix(coefficients);
  results = cloneMatrix(results);
  const rank = coefficients.length;
  const numVectors = results.length;
  const cols = new Array(rank).fill(0);
  const rows = new Array(rank).fill(0);
  const pivots = new Array(rank).fill(0);
  let col = 0;
  let row = 0;

  for (let i = 0; i < rank; i++) {
    let max = 0;

    for (let j = 0; j < rank; j++) {
      if (pivots[j] !== 1) {
        for (let k = 0; k < rank; k++) {
          if (pivots[k] !== 0) {
            if (pivots[k] > 1) {
              unsolvable();
            }
          } else if (Math.abs(coefficients[j][k]) >= max) {
            max = Math.abs(coefficients[j][k]);
            row = j;
            col = k;
          }
        }
      }
    }

    pivots[col]++;

    if (row !== col) {
      for (let k = 0; k < rank; k++) {
        swap(coefficients, row, k, col, k);
      }

      for (let k = 0; k < numVectors; k++) {
        swap(results, k, row, k, col);
      }
    }

    rows[i] = row;
    cols[i] = col;

    if (coefficients[col][col] === 0) {
      unsolvable();
    }

    const scale = perceptibleReciprocal(coefficients[col][col]);
    coefficients[col][col] = 1;

    for (let j = 0; j < rank; j++) {
      coefficients[col][j] *= scale;
    }

    for (let j = 0; j < numVectors; j++) {
      results[j][col] *= scale;
    }

    for (let j = 0; j < rank; j++) {
      if (j !== col) {
        const scale = coefficients[j][col];
        coefficients[j][col] = 0;

        for (let k = 0; k < rank; k++) {
          coefficients[j][k] -= scale * coefficients[col][k];
        }

        for (let k = 0; k < numVectors; k++) {
          results[k][j] -= scale * results[k][col];
        }
      }
    }
  }

  for (let j = rank - 1; j >= 0; j--) {
    if (cols[j] !== rows[j]) {
      for (let i = 0; i < rank; i++) {
        swap(coefficients, i, rows[j], i, cols[j]);
      }
    }
  }

  return results;
}

/**
 * Swaps matrix values by given row and col.
 *
 * @internal
 * @param array
 * @param row1
 * @param col1
 * @param row2
 * @param col2
 */
function swap(
  array: Matrix,
  row1: number,
  col1: number,
  row2: number,
  col2: number
): void {
  if (array[row1][col1] !== array[row2][col2]) {
    array[row1][col1] += array[row2][col2];
    array[row2][col2] = array[row1][col1] - array[row2][col2];
    array[row1][col1] -= array[row2][col2];
  }
}

function cloneMatrix(matrix: Matrix): Matrix {
  return matrix.map((vector) => vector.slice());
}

function unsolvable() {
  throw new TypeError("Can't solve given matrix using Gauss-Jordan method");
}

function validate(matrix: Matrix, vectors: Matrix): void | never {
  const rank = matrix.length;

  matrix.forEach((vector) => {
    if (vector.length !== rank) {
      throw new TypeError("Matrix must be square");
    }
  });

  vectors.forEach((vector) => {
    if (vector.length !== rank) {
      throw new TypeError(
        "Augment matrix vector length must be same as matrix rank"
      );
    }
  });
}
